// src/shared_new/sections/ResultsSection.jsx
// Complete Results Section with Cost Analysis, Image, Table, and Waterfall Chart

import React from "react";
import { Box, Typography, Paper, Tabs, Tab, Grid } from "@mui/material";
import ImageWithRegenerate from "../modules/ImageWithRegenerate";
import PerlCostTable from "../components/PerlCostTable";
import CostWaterfall from "../components/CostWaterfall";
import { theme } from "../../design/theme";
import FooterSection from "../sections/FooterSection";
import InfoNotification from "../modules/InfoNotification";
const { perlColors, perlTypography } = theme.custom;

/**
 * ResultsSection - Display cost analysis results with image, table, and waterfall chart
 *
 * @param {Object} props
 * @param {Object} props.costData - Cost analysis result with breakdown
 * @param {Object} props.riskData - Risk analysis result
 * @param {Object} props.substituteData - Substitute recommendations result
 * @param {string} props.productImageUrl - Product image URL
 * @param {boolean} props.generatingImage - Image generation loading state
 * @param {Function} props.onRegenerateImage - Callback for regenerating image
 * @param {string} props.productName - Product name
 * @param {Object} props.productData - Product data with user input values
 * @param {string} props.brandColor - Brand color (default: #ff6034)
 * @param {Array} props.assumptions - Array of assumption strings
 * @param {Object} props.keyInsights - Key insights object with cost_savings and strategic arrays
 * @param {Array} props.costRows - Cost table rows: [{ key, label, value }]
 * @param {number} props.costTotal - Optional cost total override for table percentage calculations
 * @param {Array} props.costRows - Cost table rows: [{ key, label, value }]
 * @param {number} props.costTotal - Optional cost total override for table percentage calculations
 * @param {boolean} props.showFormulationHint - Whether to mention Formulation in the cost-update hint
 * @param {string|null} props.noCostModelMessage - Optional message shown when costData is unavailable
 * @param {Function|null} props.onNavigateToPlayBook - Optional callback to switch to the PlayBook section
 */
export default function ResultsSection({
  costData,
  costRows = [],
  costTotal = undefined,
  riskData,
  substituteData,
  productImageUrl,
  generatingImage = false,
  onRegenerateImage,
  imageError = "",
  onRetryImage,
  productName = "Product",
  productData = null,
  brandColor = "#ff6034",
  assumptions = [],
  keyInsights = null,
  showFormulationHint = true,
  renderedAssumptions = null,
  ohpRate = null,
  substituteSectionTitle = "Ingredient Substitutes",
  noCostModelMessage = null,
  onNavigateToPlayBook = null,
}) {
  const [activeTab, setActiveTab] = React.useState(0);

  const riskSummary =
    riskData?.summary || riskData?.risk_summary || riskData?.overall_summary || null;
  const riskOverallScore =
    riskData?.overall_risk_score || riskData?.overall_score || riskData?.risk_score || null;
  const supplyChainRisks = riskData?.supply_chain_risks || riskData?.supplyChainRisks || [];
  const costRisks = riskData?.cost_risks || riskData?.costRisks || [];
  const topConcerns = riskData?.top_concerns || riskData?.topConcerns || [];
  const riskItems = riskData?.risk_items || riskData?.risks || [];

  const riskCategories = React.useMemo(() => {
    const matrix = riskData?.matrix || null;
    const categories =
      matrix?.categories ||
      matrix?.category_scores ||
      riskData?.category_scores ||
      riskData?.risk_category_scores ||
      null;
    if (!categories || typeof categories !== "object") return [];
    return Object.entries(categories)
      .map(([label, value]) => ({
        label,
        score: Number(value),
      }))
      .filter((c) => Number.isFinite(c.score));
  }, [riskData]);

  const resolvedRows = costRows || [];
  const resolvedTotal = Number.isFinite(Number(costTotal))
    ? Number(costTotal)
    : resolvedRows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  const substituteSummary =
    substituteData?.overall_recommendations || substituteData?.summary || null;
  const substitutesList =
    substituteData?.substitutes ||
    substituteData?.ideas ||
    (Array.isArray(substituteData) ? substituteData : []);
  const substituteRisks = substituteData?.risk_factors || [];
  const substitutePrioritization = substituteData?.prioritization || [];
  const substituteAssumptions = substituteData?.assumptions || null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const isPlayBookNavigable = typeof onNavigateToPlayBook === "function";
  const handlePlayBookNavigation = () => {
    if (isPlayBookNavigable) {
      onNavigateToPlayBook();
    }
  };

  // Tab configuration
  const tabs = [
    { label: "Cost Breakdown", id: "intelligent" },
    { label: "Risk Analysis", id: "risk" },
    { label: "Substitutes", id: "substitute" },
  ];

  const hasReportContent =
    !!costData ||
    !!riskData ||
    !!substituteData ||
    !!productImageUrl ||
    !!renderedAssumptions ||
    !!keyInsights;

  const reportPayload = React.useMemo(
    () =>
      JSON.stringify({
        productName,
        productData,
        productImageUrl,
        costRows: resolvedRows,
        costTotal: resolvedTotal,
        brandColor,
      }),
    [brandColor, productData, productImageUrl, productName, resolvedRows, resolvedTotal],
  );

  return (
    <Box data-report-export-root="true" data-report-ready={hasReportContent ? "true" : "false"}>
      <script type="application/json" data-report-payload>
        {reportPayload}
      </script>
      {/* Tabs Navigation */}
      <Box sx={{ mb: 3, borderBottom: `1px solid ${perlColors.border}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              color: perlColors.textSecondary,
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: perlTypography.family,
              letterSpacing: "0.3px",
              "&.Mui-selected": {
                color: brandColor,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: brandColor,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {/* Cost Breakdown Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Message to update formulation/playbook if needed */}
            {costData && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: `${brandColor}10`,
                  border: `1px solid ${brandColor}40`,
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontFamily: perlTypography.family,
                    color: perlColors.textPrimary,
                  }}
                >
                  To update the cost analysis, modify the{" "}
                  {/* {showFormulationHint && (
                    <>
                      <Box component="span" sx={{ color: brandColor, fontWeight: 600 }}>
                        Formulation
                      </Box>{" "}
                      or{" "}
                    </>
                  )} */}
                  <Box
                    component="span"
                    role={isPlayBookNavigable ? "button" : undefined}
                    tabIndex={isPlayBookNavigable ? 0 : undefined}
                    onClick={isPlayBookNavigable ? handlePlayBookNavigation : undefined}
                    onKeyDown={
                      isPlayBookNavigable
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handlePlayBookNavigation();
                            }
                          }
                        : undefined
                    }
                    sx={{
                      color: brandColor,
                      fontWeight: 600,
                      cursor: isPlayBookNavigable ? "pointer" : "default",
                      textDecoration: isPlayBookNavigable ? "underline" : "none",
                    }}
                  >
                    PlayBook
                  </Box>{" "}
                  parameters and click 'Quick Cost' to recalculate
                </Typography>
              </Box>
            )}

            {!costData && noCostModelMessage && (
              <InfoNotification
                title="Cost model not available"
                message={noCostModelMessage}
                variant="info"
                show={true}
              />
            )}

            {/* Split Layout: Image on Left, Table + Chart on Right */}
            <Box data-report-cost-layout="true" sx={{ display: "flex", gap: 3, mb: 3 }}>
              {/* Left Side - Product Image */}
              <ImageWithRegenerate
                imageUrl={productImageUrl}
                loading={generatingImage}
                onRegenerate={onRegenerateImage}
                imageError={imageError}
                onRetryImage={onRetryImage}
                productName={productName}
                brandColor={brandColor}
                height={540}
              />

              {/* Right Side - Cost Table + Chart Stacked */}
              <Box
                data-report-cost-details="true"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "540px",
                  overflowX: "auto",
                  minWidth: 0, // Allow flex item to shrink below content size
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: perlColors.background,
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: perlColors.border,
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: perlColors.textSecondary,
                    },
                  },
                }}
              >
                {/* Cost Table - Takes remaining space */}
                <Box
                  data-report-cost-table="true"
                  sx={{
                    flex: 1,
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: "500px",
                  }}
                >
                  <PerlCostTable
                    breakdown={costData?.breakdown}
                    rows={resolvedRows}
                    total={resolvedTotal}
                    brandColor={brandColor}
                    ohpRate={ohpRate}
                  />
                </Box>

                {/* Waterfall Chart - Fixed height */}
                <Box
                  data-report-cost-chart="true"
                  sx={{ height: "240px", flexShrink: 0, minWidth: "500px" }}
                >
                  <CostWaterfall breakdown={costData?.breakdown} rows={resolvedRows} />
                </Box>
              </Box>
            </Box>

            {/* Assumptions Section */}
            <Box data-report-assumptions="true">
              {renderedAssumptions ? (
                renderedAssumptions
              ) : (
                <InfoNotification
                  title="Product Information & Assumptions"
                  message="This section displays the product details and key assumptions used in the cost analysis. These values are derived from your inputs and AI-generated parameters."
                  variant="info"
                  show={true}
                />
              )}
            </Box>

            {/* Key Insights Section */}
            {keyInsights &&
              (keyInsights.cost_savings?.length > 0 || keyInsights.strategic?.length > 0) && (
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: perlColors.surface,
                    border: `1px solid ${perlColors.border}`,
                    borderRadius: 2,
                    mt: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: perlColors.textPrimary,
                    }}
                  >
                    Key Insights
                  </Typography>

                  {/* Cost Savings */}
                  {keyInsights.cost_savings?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontSize: "13px",
                          fontWeight: 500,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Cost Savings Opportunities
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {keyInsights.cost_savings.map((insight, idx) => {
                          // Handle both string and object formats
                          if (typeof insight === "string") {
                            return (
                              <Typography
                                key={idx}
                                component="li"
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  fontSize: "14px",
                                  fontFamily: perlTypography.family,
                                  color: perlColors.textSecondary,
                                  lineHeight: "20px",
                                  letterSpacing: "0.3px",
                                }}
                              >
                                {insight}
                              </Typography>
                            );
                          }

                          // Handle object format: {icon, savings, action}
                          return (
                            <Typography
                              key={idx}
                              component="li"
                              variant="body2"
                              sx={{
                                mb: 1,
                                fontSize: "14px",
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                lineHeight: "20px",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {insight.icon && <span>{insight.icon} </span>}
                              <strong>{insight.savings}</strong> {insight.action}
                            </Typography>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Strategic Insights */}
                  {keyInsights.strategic?.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          fontSize: "13px",
                          fontWeight: 500,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Strategic Recommendations
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {keyInsights.strategic.map((insight, idx) => (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {insight}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
            {/* Footer added here */}
            <Paper
              sx={{
                p: 1,
                bgcolor: perlColors.surface,
                border: `1px solid ${perlColors.border}`,
                borderRadius: 2,
                mt: 3,
              }}
            >
              <FooterSection />
            </Paper>
          </Box>
        )}

        {/* Risk Analysis Tab */}
        {activeTab === 1 && (
          <Box>
            {riskData ? (
              <Box>
                {/* Risk Summary */}
                {riskSummary && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Risk Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: "20px",
                      }}
                    >
                      {riskSummary}
                    </Typography>
                  </Paper>
                )}

                {/* Overall Risk Score */}
                {riskOverallScore && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Overall Risk Score: {riskOverallScore}/10
                    </Typography>
                  </Paper>
                )}

                {/* Risk Category Scores (matrix) */}
                {riskCategories.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Risk Category Scores
                    </Typography>
                    <Grid container spacing={2}>
                      {riskCategories.map((cat, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={`${cat.label}-${idx}`}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${perlColors.border}`,
                              bgcolor: perlColors.surface,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                textTransform: "capitalize",
                              }}
                            >
                              {cat.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 700,
                                fontFamily: perlTypography.family,
                                color: brandColor,
                              }}
                            >
                              {cat.score}/10
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}

                {/* Supply Chain Risks */}
                {supplyChainRisks && supplyChainRisks.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Supply Chain Risks
                    </Typography>
                    {supplyChainRisks.map((risk, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: perlTypography.family,
                            color: perlColors.textPrimary,
                            mb: 0.5,
                          }}
                        >
                          {risk.ingredient || risk.area} - Severity: {risk.severity}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                          }}
                        >
                          {risk.description || risk.risk}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Cost Risks */}
                {costRisks && costRisks.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Cost Risks
                    </Typography>
                    {costRisks.map((risk, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: perlTypography.family,
                            color: perlColors.textPrimary,
                            mb: 0.5,
                          }}
                        >
                          {risk.area || risk.ingredient} - Severity: {risk.severity}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                          }}
                        >
                          {risk.description || risk.risk}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Risk Items */}
                {riskItems &&
                  riskItems.length > 0 &&
                  (!supplyChainRisks.length || !costRisks.length) && (
                    <Paper
                      sx={{
                        p: 3,
                        mb: 3,
                        bgcolor: perlColors.surface,
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 2,
                          fontSize: "14px",
                          fontWeight: 600,
                          fontFamily: perlTypography.family,
                          color: perlColors.textPrimary,
                        }}
                      >
                        Risk Items
                      </Typography>
                      {riskItems.map((risk, idx) => (
                        <Box key={idx} sx={{ mb: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: perlColors.textPrimary,
                              }}
                            >
                              {risk.area || risk.ingredient || risk.title || "Risk"}
                              {risk.severity ? ` - Severity: ${risk.severity}` : ""}
                            </Box>
                            {(risk.description || risk.risk || risk.detail) ? ": " : ""}
                            {risk.description || risk.risk || risk.detail || ""}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}

                {/* Top Concerns */}
                {topConcerns && topConcerns.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Top Concerns
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {topConcerns.map((concern, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                          }}
                        >
                          {concern}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                )}
                {/* Footer added here */}
                <Paper
                  sx={{
                    p: 1,
                    bgcolor: perlColors.surface,
                    border: `1px solid ${perlColors.border}`,
                    borderRadius: 2,
                    mt: 3,
                  }}
                >
                  <FooterSection />
                </Paper>
              </Box>
            ) : (
              <Typography sx={{ color: perlColors.textSecondary }}>
                Risk analysis data not available
              </Typography>
            )}
          </Box>
        )}

        {/* Substitutes Tab */}
        {activeTab === 2 && (
          <Box>
            {substituteData ? (
              <Box>
                {/* Overall Recommendations */}
                {substituteSummary && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Overall Recommendations
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: "20px",
                      }}
                    >
                      {substituteSummary}
                    </Typography>
                  </Paper>
                )}

                {/* Estimated Total Savings */}
                {substituteData.estimated_total_savings && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: brandColor,
                      }}
                    >
                      Estimated Total Savings: {substituteData.estimated_total_savings}
                    </Typography>
                  </Paper>
                )}

                {/* Substitutes List */}
                {substitutesList && substitutesList.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mb: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      {substituteSectionTitle}
                    </Typography>
                    {substitutesList.map((sub, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          mb: 3,
                          pb: 3,
                          borderBottom:
                            idx < substitutesList.length - 1
                              ? `1px solid ${perlColors.border}`
                              : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            fontFamily: perlTypography.family,
                            color: perlColors.textPrimary,
                            mb: 1,
                          }}
                        >
                          {sub.original_ingredient || sub.current_item}
                          {sub.substitute || sub.proposed_item}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            mb: 1,
                          }}
                        >
                          {sub.reason || sub.risk_note || "No rationale provided."}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: brandColor,
                            }}
                          >
                            Savings:{" "}
                            {sub.estimated_savings ?? sub.estimated_cost_delta_per_unit ?? "N/A"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                            }}
                          >
                            Impact: {sub.impact_on_quality || "Minimal"}
                          </Typography>
                          {Number.isFinite(Number(sub.feasibility_score)) && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                              }}
                            >
                              Feasibility: {sub.feasibility_score}/10
                            </Typography>
                          )}
                          {Number.isFinite(Number(sub.timeline_weeks)) && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                              }}
                            >
                              Timeline: {sub.timeline_weeks} weeks
                            </Typography>
                          )}
                        </Box>
                        {Array.isArray(sub.validation_steps) && sub.validation_steps.length > 0 && (
                          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                            {sub.validation_steps.map((step, stepIdx) => (
                              <Typography
                                key={stepIdx}
                                component="li"
                                variant="body2"
                                sx={{
                                  mb: 0.5,
                                  fontSize: "13px",
                                  fontFamily: perlTypography.family,
                                  color: perlColors.textSecondary,
                                  lineHeight: "18px",
                                }}
                              >
                                {step}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Risk Factors */}
                {substituteRisks && substituteRisks.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Risk Factors
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {substituteRisks.map((risk, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                          }}
                        >
                          {risk}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Prioritization */}
                {substitutePrioritization && substitutePrioritization.length > 0 && (
                  <Paper
                    sx={{
                      p: 3,
                      mt: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Prioritization
                    </Typography>
                    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                      {substitutePrioritization.map((item, idx) => (
                        <Typography
                          key={`${item}-${idx}`}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Assumptions */}
                {substituteAssumptions && (
                  <Paper
                    sx={{
                      p: 3,
                      mt: 3,
                      bgcolor: perlColors.surface,
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: perlTypography.family,
                        color: perlColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontFamily: perlTypography.family,
                        color: perlColors.textSecondary,
                        lineHeight: "20px",
                      }}
                    >
                      {substituteAssumptions}
                    </Typography>
                  </Paper>
                )}

                {/* Footer added here */}
                <Paper
                  sx={{
                    p: 1,
                    bgcolor: perlColors.surface,
                    border: `1px solid ${perlColors.border}`,
                    borderRadius: 2,
                    mt: 3,
                  }}
                >
                  <FooterSection />
                </Paper>
              </Box>
            ) : (
              <Typography sx={{ color: perlColors.textSecondary }}>
                Substitute recommendations not available
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
